import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Visibility from "@mui/icons-material/Visibility";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { ConfirmDeleteCredential } from "components/Credentials/ConfirmDeleteCredential";
import { CredentialDialog } from "components/Credentials/CredentialDialog";
import { CredentialVerificationStatusChip } from "components/CredentialVerificationStatusChip";
import { parseLepcJurisdiction } from "encamp-shared/src/portal/parseLepcJurisdiction";
import {
  CredentialKind,
  CredentialsQuery,
  CredentialVerificationStatus,
  Permission,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import useFacilityOverview from "hooks/useFacilityOverview";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { credentialKindToLabel, credentialVerificationStatusToLabel } from "util/constants";

import { CREDENTIALS } from "./schema";

export type Row = CredentialsQuery["credentials"]["items"][number];

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "name", sort: "asc" }];

const STATE_FILTER_KEY: FilterKey = { key: "state", header: "State/Territory" };
const FACILITY_FILTER_KEY: FilterKey = { key: "facility", header: "Facility" };
const TENANT_ADDITIONAL_FILTER_KEYS: FilterKey[] = [STATE_FILTER_KEY, FACILITY_FILTER_KEY];
const FACILITY_ADDITIONAL_FILTER_KEYS: FilterKey[] = [STATE_FILTER_KEY];

const TENANT_EXCLUDE_FILTER_COLUMNS = ["facilities", "portal.jurisdiction"];
const FACILITY_EXCLUDE_FILTER_COLUMNS = ["facility", "facilities", "portal.jurisdiction"];

const INITIAL_COLUMN_VISIBILITY = {
  // Hidden by default; staff can enable it from the column picker
  usesEManifestApi: false,
};
const MOBILE_COLUMN_VISIBILITY = {
  usesEManifestApi: false,
  name: false,
  facilities: false,
  verificationStatus: false,
  email: false,
};

const CREDENTIAL_KIND_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(CredentialKind).map((kind) => [kind, credentialKindToLabel(kind)])
);
const CREDENTIAL_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  Object.values(CredentialVerificationStatus).map((status) => [
    status,
    credentialVerificationStatusToLabel(status),
  ])
);

const getItems = (data: CredentialsQuery) => data.credentials.items;
const getCount = (data: CredentialsQuery) => data.credentials.count ?? 0;

function StateTerritoryCell({ row }: { row: Row }) {
  if (!row.portal) return "";

  if (row.kind === CredentialKind.Lepc) {
    return (
      <Tooltip title={row.portal.description}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="body2">
            {parseLepcJurisdiction(row.portal.jurisdiction)?.state}
          </Typography>
          <InfoIcon color="disabled" fontSize="small" />
        </Stack>
      </Tooltip>
    );
  }
  return row.portal.jurisdiction ?? "";
}

function buildColumns({
  isStaff,
  canEditCredentials,
  onEdit,
  onDelete,
}: {
  isStaff: boolean;
  canEditCredentials: boolean;
  onEdit: (id: string) => void;
  onDelete: (row: Row) => void;
}): OmnisearchGridColDef<Row>[] {
  const cols: OmnisearchGridColDef<Row>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      sortable: true,
    },
    {
      field: "kind",
      headerName: "Type",
      flex: 1,
      sortable: true,
      valueGetter(_, row) {
        return credentialKindToLabel(row.kind);
      },
      filterKeyType: "enumAutocomplete",
      enumValues: Object.values(CredentialKind),
      enumPresentationFunction: (enumValue) => CREDENTIAL_KIND_LABELS[enumValue] ?? enumValue,
    },
    {
      field: "facilities",
      headerName: "Facilities",
      flex: 1,
      renderCell({ row }) {
        const facilities = row.facilityCount ? row.facilityCount : "";
        return (
          <Stack>
            <Typography variant="body2">{facilities}</Typography>
          </Stack>
        );
      },
    },
    {
      field: "portal.jurisdiction",
      headerName: "State/Territory",
      sortable: false,
      flex: 1,
      renderCell({ row }) {
        return <StateTerritoryCell row={row} />;
      },
    },
  ];

  if (isStaff) {
    cols.push(
      {
        field: "verificationStatus",
        headerName: "Verification Status",
        flex: 1,
        valueGetter(_, row) {
          return credentialVerificationStatusToLabel(row.verificationStatus);
        },
        renderCell: ({ row }) => (
          <CredentialVerificationStatusChip status={row.verificationStatus} />
        ),
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(CredentialVerificationStatus),
        enumPresentationFunction: (enumValue) => CREDENTIAL_STATUS_LABELS[enumValue] ?? enumValue,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
      },
      {
        field: "usesEManifestApi",
        headerName: "e-Manifest API",
        flex: 1,
        sortable: false,
        filterKeyType: "boolean",
        valueGetter(_, row) {
          return row.usesEManifestApi ? "Yes" : "";
        },
      }
    );
  }

  cols.push({
    field: "actions",
    type: "actions",
    align: "left",
    getActions: ({ row }) => {
      // Users who can't edit get a read-only "view" affordance (eye) instead
      // of the edit pencil, and no delete action.
      const viewOrEditAction = canEditCredentials ? (
        <Tooltip title="Edit Credential" key="edit">
          <GridActionsCellItem
            onClick={() => onEdit(row.id)}
            label="Edit Credential"
            icon={<Edit />}
          />
        </Tooltip>
      ) : (
        <Tooltip title="View Credential" key="view">
          <GridActionsCellItem
            onClick={() => onEdit(row.id)}
            label="View Credential"
            icon={<Visibility />}
          />
        </Tooltip>
      );

      const actions = [viewOrEditAction];

      if (canEditCredentials) {
        actions.push(
          <Tooltip title="Delete Credential" key="delete">
            <GridActionsCellItem
              onClick={() => onDelete(row)}
              label="Delete Credential"
              icon={<Delete />}
            />
          </Tooltip>
        );
      }

      return actions;
    },
  });

  return cols;
}

export function Credentials() {
  const { tenantId, facilityId, credentialId } = useParams();
  const { isStaff } = useCurrentUser();
  const { hasPermissions } = useAuthorization();
  // WriteCredential gates create/edit/delete. Revealing the password is a
  // separate grant (ViewCredentials), enforced server-side, so a user can have
  // read-only access that still shows the password.
  const canEditCredentials = useMemo(
    () => hasPermissions([Permission.WriteCredential]) || isStaff,
    [hasPermissions, isStaff]
  );
  const location = useLocation();
  const navigate = useNavigate();
  const { data: facilityData } = useFacilityOverview(facilityId);

  const [confirmDeleteCredential, setConfirmDeleteCredential] = useState<Row | undefined>(
    undefined
  );

  const pushCredentialIdToUrl = useCallback(
    (id: string) => {
      navigate(`${id}${location.search}`);
    },
    [navigate, location.search]
  );

  const removeCredentialIdFromUrl = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    pathSegments.pop(); // Remove the last segment
    const newPath = `/${pathSegments.join("/")}${location.search}`;
    navigate(newPath);
  };

  useBreadcrumb(
    facilityId
      ? [
          {
            label: "Facilities",
            to: `/o/${tenantId}/facilities`,
          },
          {
            label: facilityData?.facility?.name ?? "",
            to: `/o/${tenantId}/facilities/${facilityId}/profile`,
          },
          {
            label: "Credentials",
          },
        ]
      : [{ label: "Credentials" }]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        isStaff,
        canEditCredentials,
        onEdit: pushCredentialIdToUrl,
        onDelete: setConfirmDeleteCredential,
      }),
    [isStaff, canEditCredentials, pushCredentialIdToUrl]
  );

  const commandButtons = useMemo(() => {
    if (!canEditCredentials) return [];
    return [
      <Button
        key="add-credential-button"
        variant="contained"
        onClick={() => pushCredentialIdToUrl("add")}
      >
        Add Credential
      </Button>,
    ];
  }, [canEditCredentials, pushCredentialIdToUrl]);

  const buildVariables = useCallback(
    (omnisearch: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => {
      const defaultSearch = `tenantId:${tenantId}${facilityId ? ` facilityId:${facilityId}` : ""}`;
      return {
        search: omnisearch ? `${omnisearch} ${defaultSearch}` : defaultSearch,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      };
    },
    [tenantId, facilityId]
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<Row>) => pushCredentialIdToUrl(params.row.id),
    [pushCredentialIdToUrl]
  );

  return (
    <>
      <OmnisearchDataGridV2
        query={CREDENTIALS}
        persistenceKey="credentials"
        columns={columns}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        noRowsMessage={
          facilityId
            ? "No credentials have been added to this facility yet."
            : "No credentials have been added to this organization yet."
        }
        onRowClick={handleRowClick}
        commandButtons={commandButtons}
        rowHeight="auto"
        additionalFilterKeys={
          facilityId ? FACILITY_ADDITIONAL_FILTER_KEYS : TENANT_ADDITIONAL_FILTER_KEYS
        }
        excludeFilterColumns={
          facilityId ? FACILITY_EXCLUDE_FILTER_COLUMNS : TENANT_EXCLUDE_FILTER_COLUMNS
        }
        initialColumnVisibilityModel={INITIAL_COLUMN_VISIBILITY}
        mobileColumnVisibilityModel={MOBILE_COLUMN_VISIBILITY}
        tenantId={tenantId}
      />
      {credentialId && credentialId !== "add" && !credentialId.startsWith("delete/") && (
        <CredentialDialog
          onClose={removeCredentialIdFromUrl}
          mode="edit"
          credentialId={credentialId}
          currentFacility={facilityData?.facility}
        />
      )}
      {credentialId === "add" && (
        <CredentialDialog
          onClose={removeCredentialIdFromUrl}
          mode="add"
          currentFacility={facilityData?.facility}
        />
      )}
      {confirmDeleteCredential && (
        <ConfirmDeleteCredential
          credentialId={confirmDeleteCredential.id}
          onClose={() => setConfirmDeleteCredential(undefined)}
        />
      )}
    </>
  );
}
